import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { pdfExportService } from '../../../services/pdfExportService';

const CompletionHistory = ({ completions, sites, selectedSiteId, selectedDate, onSiteChange, onDateChange }) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedStatus, setSelectedStatus] = React.useState('all');
  const [exportingId, setExportingId] = React.useState(null);

  const siteOptions = [
    { value: '', label: 'All Sites' },
    ...sites?.map(site => ({
      value: site?.id,
      label: site?.site_name
    }))
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'high', label: 'High Pass Rate (>80%)' },
    { value: 'medium', label: 'Medium Pass Rate (50-80%)' },
    { value: 'low', label: 'Low Pass Rate (<50%)' }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getCompletionStats = (completion) => {
    const responses = completion?.checklist_item_responses || [];
    const passCount = responses?.filter(r => r?.status === 'pass')?.length;
    const failCount = responses?.filter(r => r?.status === 'fail')?.length;
    const naCount = responses?.filter(r => r?.status === 'na')?.length;
    const totalCount = responses?.length;
    
    return { passCount, failCount, naCount, totalCount };
  };

  const getPassRate = (completion) => {
    const stats = getCompletionStats(completion);
    return stats?.totalCount > 0 ? Math.round((stats?.passCount / stats?.totalCount) * 100) : 0;
  };

  const filteredCompletions = completions?.filter(completion => {
    // Search filter
    if (searchQuery) {
      const searchLower = searchQuery?.toLowerCase();
      const matchesSearch = 
        completion?.site_checklists?.checklist_name?.toLowerCase()?.includes(searchLower) ||
        completion?.user_profiles?.full_name?.toLowerCase()?.includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Status filter based on pass rate
    if (selectedStatus !== 'all') {
      const passRate = getPassRate(completion);
      if (selectedStatus === 'high' && passRate <= 80) return false;
      if (selectedStatus === 'medium' && (passRate < 50 || passRate > 80)) return false;
      if (selectedStatus === 'low' && passRate >= 50) return false;
    }

    return true;
  });

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedStatus('all');
  };

  const hasActiveFilters = searchQuery || selectedStatus !== 'all';

  const handleExportPDF = async (completion) => {
    try {
      setExportingId(completion?.id);
      const responses = completion?.checklist_item_responses || [];
      const items = completion?.site_checklists?.checklist_items || [];
      await pdfExportService?.generateChecklistCompletionReport({
        completion,
        items,
        responses,
        siteName: sites?.find(s => s?.id === completion?.site_id)?.site_name || '',
        checklistName: completion?.site_checklists?.checklist_name || '',
        companyName: '',
        completedBy: completion?.user_profiles?.full_name || '',
        completedAt: completion?.completion_date,
      });
    } catch (err) {
      console.error('PDF export error:', err);
    } finally {
      setExportingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <h2 className="text-lg font-semibold text-foreground">Completion History</h2>
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="text-sm text-accent hover:text-accent/80 font-medium flex items-center gap-1"
            >
              <Icon name="X" size={14} />
              Clear Filters
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search checklists..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="pl-10"
            />
          </div>
          <div className="w-full">
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => onDateChange(e?.target?.value)}
            />
          </div>
          <Select
            options={siteOptions}
            value={selectedSiteId}
            onChange={onSiteChange}
            placeholder="Filter by site"
          />
          <Select
            options={statusOptions}
            value={selectedStatus}
            onChange={setSelectedStatus}
          />
        </div>
      </div>

      {filteredCompletions?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-12 text-center">
          <Icon name="ClipboardList" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {hasActiveFilters ? 'No Matching Completions' : 'No Completions Found'}
          </h3>
          <p className="text-sm text-muted-foreground">
            {hasActiveFilters
              ? 'Try adjusting your search or filter criteria.'
              : selectedSiteId 
                ? 'No checklists have been completed for this site on the selected date.' 
                : 'Select a site and date to view completion history.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredCompletions?.map((completion) => {
            const stats = getCompletionStats(completion);
            const completionPercentage = getPassRate(completion);

            return (
              <div key={completion?.id} className="bg-card border border-border rounded-lg shadow-elevation-2 p-4 md:p-6">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-foreground mb-1">
                      {completion?.site_checklists?.checklist_name}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Icon name="Calendar" size={14} />
                        <span>{formatDate(completion?.completion_date)}</span>
                      </div>
                      {completion?.user_profiles?.full_name && (
                        <div className="flex items-center gap-1">
                          <Icon name="User" size={14} />
                          <span>Completed by {completion?.user_profiles?.full_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <div className="text-2xl font-bold text-accent">{completionPercentage}%</div>
                      <div className="text-xs text-muted-foreground">Pass Rate</div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName={exportingId === completion?.id ? 'Loader' : 'Download'}
                      onClick={() => handleExportPDF(completion)}
                      disabled={exportingId === completion?.id}
                    >
                      {exportingId === completion?.id ? 'Exporting...' : 'Export PDF'}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="p-3 bg-success/10 rounded-lg">
                    <div className="text-2xl font-bold text-success">{stats?.passCount}</div>
                    <div className="text-xs text-muted-foreground">Passed</div>
                  </div>
                  <div className="p-3 bg-error/10 rounded-lg">
                    <div className="text-2xl font-bold text-error">{stats?.failCount}</div>
                    <div className="text-xs text-muted-foreground">Failed</div>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <div className="text-2xl font-bold text-muted-foreground">{stats?.naCount}</div>
                    <div className="text-xs text-muted-foreground">N/A</div>
                  </div>
                </div>

                {completion?.notes && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-foreground mb-1">Notes</h4>
                    <p className="text-sm text-muted-foreground">{completion?.notes}</p>
                  </div>
                )}

                {completion?.signature_data && (
                  <div className="pt-4 border-t border-border">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="PenTool" size={14} />
                      <span>Signed by: <span className="font-medium text-foreground">{completion?.signature_data}</span></span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CompletionHistory;